import React, { createContext, useContext, useState, ReactNode } from 'react';
import {
  INITIAL_SUBSCRIPTION_PRODUCTS,
  loadSubscriptionProductsFromStorage,
  type Plan,
  type ProductConfig
} from '@shared/subscriptionConfig';

export type { Plan, ProductConfig };

interface SubscriptionContextType {
  products: ProductConfig[];
  setProducts: React.Dispatch<React.SetStateAction<ProductConfig[]>>;
  getProductConfig: (productId: string) => ProductConfig | undefined;
  getPlanById: (productId: string, planId: string) => Plan | undefined;
  updatePlan: (productId: string, planId: string, updatedPlan: Partial<Plan>) => void;
  toggleBilling: (productId: string) => void;
  addPlan: (productId: string, newPlan: Plan) => void;
  removePlan: (productId: string, planId: string) => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<ProductConfig[]>(() => {
    const stored = loadSubscriptionProductsFromStorage();
    return stored ?? INITIAL_SUBSCRIPTION_PRODUCTS;
  });

  const getProductConfig = (productId: string) => {
    return products.find(p => p.id === productId);
  };

  const getPlanById = (productId: string, planId: string) => {
    const product = getProductConfig(productId);
    return product?.plans.find(p => p.id === planId);
  };

  const updatePlan = (productId: string, planId: string, updatedPlan: Partial<Plan>) => {
    setProducts(products.map(product => {
      if (product.id === productId) {
        return {
          ...product,
          plans: product.plans.map(plan => 
            plan.id === planId 
              ? { ...plan, ...updatedPlan }
              : plan
          )
        };
      }
      return product;
    }));
  };

  const toggleBilling = (productId: string) => {
    setProducts(products.map(p => 
      p.id === productId 
        ? { ...p, billingEnabled: !p.billingEnabled }
        : p
    ));
  };

  const addPlan = (productId: string, newPlan: Plan) => {
    setProducts(products.map(product => {
      if (product.id === productId) {
        return {
          ...product,
          plans: [...product.plans, newPlan]
        };
      }
      return product;
    }));
  };

  const removePlan = (productId: string, planId: string) => {
    setProducts(products.map(product => {
      if (product.id === productId) {
        return {
          ...product,
          plans: product.plans.filter(p => p.id !== planId)
        };
      }
      return product;
    }));
  };

  return (
    <SubscriptionContext.Provider
      value={{
        products,
        setProducts,
        getProductConfig,
        getPlanById,
        updatePlan,
        toggleBilling,
        addPlan,
        removePlan
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
